import { HomeIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { getFormNameByWorkflowUser } from "@/api/form";
import { getAllMenuRoleItems } from "@/api/menu";
import {
  createWorkFlowUser,
  getWorkFlowUserWorkflow // removeWorkFlowUser
} from "@/api/workflow-user";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { cn } from "@/lib/utils";

import { MenuRoleItemType } from "@/types/menu-item";

import { useActiveRoleStore } from "@/hooks/store/use-active-role-store";
import useBreadcrumbStore from "@/hooks/store/use-breadcrumb";
import { useDashboardTabsStore } from "@/hooks/store/use-dashboard-tabs-store";

import DashboardTabs from "@/components/layout/dashboard-tabs";

import { routeLabels } from "./breadcrumb-labels";

const flattenMenuRoleItems = (
  items: MenuRoleItemType[]
): MenuRoleItemType[] => {
  return items.reduce<MenuRoleItemType[]>((acc, current) => {
    acc.push(current);

    if (current.childs?.length) {
      acc.push(...flattenMenuRoleItems(current.childs));
    }

    return acc;
  }, []);
};

const getSearchOptionValue = (item: MenuRoleItemType) => {
  if (item.link) return `link:${item.link}`;
  if (item.workflow?.id) return `workflow:${item.workflow.id}`;
  return `name:${item.name}`;
};

const Breadcrumb: React.FC = () => {
  const navigate = useNavigate();

  const { breadcrumbs, setBreadcrumbs, isSidebarOpen } = useBreadcrumbStore();

  const { activeRoleId } = useActiveRoleStore();

  const { openTab } = useDashboardTabsStore();

  const numericActiveRoleId = activeRoleId ? parseInt(activeRoleId, 10) : 0;

  const [selectedMenuValue, setSelectedMenuValue] = useState<string>("");

  const [isSearchPending, setIsSearchPending] = useState(false);

  const matchDynamicRoute = (path: string) => {
    const entries = Object.entries(routeLabels);

    for (const [pattern, label] of entries) {
      const regex = new RegExp("^" + pattern.replace(/:[^/]+/g, "[^/]+") + "$");

      if (regex.test(path)) {
        return label;
      }
    }

    return null;
  };

  const { data: menuItems, isLoading: isMenuLoading } = useQuery({
    queryKey: ["workflowRoles", numericActiveRoleId],
    queryFn: () =>
      getAllMenuRoleItems({
        page: 1,
        size: 0,
        activeRoleId: numericActiveRoleId
      })
  });
  const location = useLocation();
  const { tabs } = useDashboardTabsStore();
  // const currentTab = tabs.find(
  //   (tab) => tab.path.toLowerCase() === location.pathname.toLowerCase()
  // );

  // const { data:formData } = useQuery({
  //   queryKey: ['form'],
  //   queryFn: () => getEntity(Number(currentTab?.label))
  // })

  const isEntityPage =
    location.pathname.includes("/form/entities/") ||
    location.pathname.includes("/dashboard/workflow-form/");

  const workflowUserId = location.pathname.split("/").pop();

  const { data: formName } = useQuery({
    queryKey: ["formName", workflowUserId],
    queryFn: () => getFormNameByWorkflowUser(workflowUserId!),
    enabled: !!workflowUserId
  });
  console.log("formData" + formName);
  const searchableMenuItems = useMemo(() => {
    const flattened = flattenMenuRoleItems(menuItems?.data ?? []);

    return flattened.filter((item) => item.isSearchable);
  }, [menuItems]);

  const searchOptions = useMemo(
    () =>
      searchableMenuItems.map((item) => ({
        value: getSearchOptionValue(item),
        label: item.name,
        item
      })),
    [searchableMenuItems]
  );

  const selectedMenuItem = useMemo(() => {
    return (
      searchOptions.find((option) => option.value === selectedMenuValue)
        ?.item ?? null
    );
  }, [searchOptions, selectedMenuValue]);

  useEffect(() => {
    const pathnames = location.pathname.split("/").filter(Boolean);

    const breadcrumbItems = pathnames
      .map((path, index) => {
        const fullPath = `/${pathnames
          .slice(0, index + 1)
          .join("/")}`.toLowerCase();

        const existingTab = tabs.find(
          (tab) => tab.path.toLowerCase() === fullPath
        );

        return {
          path: fullPath,
          label:
            existingTab?.label ||
            routeLabels[fullPath] ||
            matchDynamicRoute(fullPath) ||
            path.charAt(0).toUpperCase() + path.slice(1)
        };
      })
      .filter((item, index) => {
        const isDashboard =
          index === 0 && item.label.toLowerCase() === "dashboard";
        const isWorkflowForm = item.path.split("/").pop() === "workflow-form";
        return !isDashboard && !isWorkflowForm;
      });

    setBreadcrumbs(breadcrumbItems);
  }, [location.pathname, setBreadcrumbs, tabs]);

  const handleSearch = async () => {
    if (!selectedMenuItem || isSearchPending) {
      return;
    }

    if (selectedMenuItem.link) {
      openTab({
        path: selectedMenuItem.link,
        label: selectedMenuItem.name
      });

      navigate(selectedMenuItem.link);

      return;
    }

    setIsSearchPending(true);

    try {
      const workflowId = selectedMenuItem.workflow?.id;

      if (!workflowId) {
        throw new Error("شناسه فرآیند یافت نشد");
      }

      // const existingWorkflow = await getWorkFlowUserWorkflow(workflowId);

      // if (existingWorkflow?.data?.id) {
      //   await removeWorkFlowUser(+existingWorkflow.data.id);
      // }

      const createdWorkFlowUser = await createWorkFlowUser({
        workFlowId: workflowId
      });

      const path = `/dashboard/workflow-form/${createdWorkFlowUser?.data.id}`;

      openTab({
        path,
        label: selectedMenuItem.name
      });

      navigate(path);
    } catch {
      throw new Error("خطا در دریافت اطلاعات");
    } finally {
      setIsSearchPending(false);
    }
  };

  return (
    <nav
      aria-label="breadcrumb"
      className="items-top mb-2 flex h-20 justify-between border-none bg-[#f1f1f3]"
    >
      <ul className="w-full">
        <ul className="flex h-12 w-full items-center space-x-2 border-[#D8E7F4] bg-white px-4">
          <li className="flex items-center justify-center">
            <Link to="/dashboard">
              <HomeIcon className="size-5" />
            </Link>

            <span className="mx-4 text-gray-500">
              <svg
                width="23"
                height="41"
                viewBox="0 0 23 41"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <line
                  x1="0.92965"
                  y1="20.788"
                  x2="21.0713"
                  y2="0.646357"
                  stroke="#D8E7F4"
                />
                <line
                  x1="1.63676"
                  y1="20.5046"
                  x2="21.7784"
                  y2="40.6463"
                  stroke="#D8E7F4"
                />
              </svg>
            </span>
          </li>

          {breadcrumbs
            .filter((item) => item.label !== "Editor")
            .map((item, index) => (
              <li key={index} className="flex items-center">
                {index !== 0 && (
                  <span className="mx-2 text-gray-500">
                    <svg
                      width="23"
                      height="41"
                      viewBox="0 0 23 41"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line
                        x1="0.92965"
                        y1="20.788"
                        x2="21.0713"
                        y2="0.646357"
                        stroke="#D8E7F4"
                      />
                      <line
                        x1="1.63676"
                        y1="20.5046"
                        x2="21.7784"
                        y2="40.6463"
                        stroke="#D8E7F4"
                      />
                    </svg>
                  </span>
                )}

                {index === breadcrumbs.length - 1 ? (
                  <>
                    <span className="font-medium text-gray-700">
                      {isEntityPage ? formName : item.label}
                    </span>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className="text-gray-700 hover:text-primary"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
        </ul>

        <ul
          className={cn(
            "hidden md:flex h-8 w-full items-center space-x-2 rounded-t-xl border-t border-[#D8E7F4] bg-[#f1f1f3] px-4 lg:px-10 pt-2 duration-500",
            isSidebarOpen && "lg:mr-[265px]"
          )}
        >
          <DashboardTabs />
        </ul>
      </ul>

      <ul className="hidden h-full w-1/5 items-center space-x-2 lg:block">
        <form
          className="flex h-14 items-center justify-center gap-3 bg-white"
          onSubmit={(event) => {
            event.preventDefault();
            handleSearch();
          }}
        >
          <div className="flex h-10 w-64 items-center gap-2 rounded-full border border-[#D8E7F4] bg-[#F8FAFD] px-4 shadow-[0_6px_16px_rgba(26,58,107,0.18)]">
            <select
              className="w-20 rounded-full border border-[#D8E7F4] p-1 px-2 text-center text-sm text-gray-700 focus:outline-none"
              disabled={
                isMenuLoading || !searchableMenuItems.length || isSearchPending
              }
              value={selectedMenuValue}
              onChange={(event) => setSelectedMenuValue(event.target.value)}
            >
              <option value="">
                {isMenuLoading ? "در حال بارگذاری..." : "همه موارد"}
              </option>

              {!isMenuLoading &&
                searchOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </select>

            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-gray-500"
            >
              <path
                d="m3 5 4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <input
              className="h-full w-full bg-transparent text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none"
              placeholder="جستجو کنید"
            />

            <button
              type="button"
              onClick={handleSearch}
              disabled={!selectedMenuItem || isSearchPending}
              className="text-primary disabled:opacity-50"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 19a8 8 0 1 1 5.3-14h0a8 8 0 0 1 1.8 10.3l4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </form>

        <div className="flex h-6 w-full items-center justify-center gap-2 rounded-br-xl border-b border-s border-[#D8E7F4] bg-white px-4">
          <button
            type="button"
            className="flex w-fit items-center text-gray-600 hover:text-primary"
          >
            <span className="flex h-5 items-center justify-center text-primary">
              <svg
                width="10"
                height="10"
                viewBox="0 0 12 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 3l6 6m0-6-6 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </span>

            <p className="text-xs">پنهان کردن نتایج جستجو</p>
          </button>

          <button
            type="button"
            className="flex items-center gap-1 text-xs text-gray-600 hover:text-primary"
          >
            <span className="text-primary">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11 19a8 8 0 1 1 5.3-14h0a8 8 0 0 1 1.8 10.3l4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            جستجوی پیشرفته
          </button>
        </div>
      </ul>
    </nav>
  );
};

export default Breadcrumb;
